import type { Activity } from "@lib/activity";
import { type LandingPageChoice } from "@lib/landing";
import { type Ticket } from "@lib/ticket";
import { useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import connectionScript from "./connection";
import { hashedEmailAtom } from "@/lib/state";
import { useAtom } from "jotai";
import { hashEmail } from "@/lib/email.client";

type TikTokProps = {
  activity: Activity;
  code: string;
  page: LandingPageChoice;
  ticket?: Ticket;
};

type TtqObj = {
  load: (code: string) => void;
  page: () => void;
  track: (event: string) => void;
  identify: (properties: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    ttq?: TtqObj;
  }
}

export function TikTok({ activity, code, page, ticket }: TikTokProps) {
  const [cookies] = useCookies([`sopost-purpose_${activity.id}`]);
  const tracked = useRef(false);
  const [hashedEmail] = useAtom(hashedEmailAtom);

  const cookie = cookies[`sopost-purpose_${activity.id}`] as string | undefined;
  const enabled = !(!cookie || cookie === "rejected");

  useEffect(() => {
    if (tracked.current || !enabled) return;
    if (!window.ttq) {
      connectionScript();
      (window.ttq as unknown as TtqObj).load(code);
    }

    tracked.current = true;
    const ttq = window.ttq as unknown as TtqObj;

    const trackAsync = async () => {
      const toTrack =
        hashedEmail ||
        (ticket?.address?.email ? await hashEmail(ticket.address.email) : null);

      if (toTrack) {
        ttq.identify({
          email: toTrack,
        });
      }

      ttq.page();

      // Track the form submission if on the completion page and
      // hashedEmail is available (which means the form was just submitted,
      // rather than a page refresh).
      if (page == "completion" && hashedEmail) {
        ttq.track("Lead");
      }
    };

    void trackAsync();
  }, [enabled]);

  return <></>;
}
